/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * Shared warnings model for every mapping editor (guidance §16).
 *
 * One taxonomy, one look. `OrtbBodyEditor` (via `validateOrtbBody`) established
 * the `error | warn | info` levels + a `fix` affordance; this generalizes them so
 * the parameter, header, response-mapping, and pixel editors surface issues
 * identically — as a row/field badge, an editor banner (`ValidationSummary`), a
 * step dot, and a wizard-footer summary.
 *
 * Severity contract — **`R3a`, ratified 2026-08-27, and nothing here blocks Save** (`#1193`):
 *  - `error` — wrong, and you may proceed. The document would not do what the operator meant.
 *  - `warn`  — this may not be what you meant (unresolved macro, empty required field,
 *              won't-populate gap, config key in a request field).
 *  - `info`  — the runtime already supplies it; no action needed.
 *
 * **This said `error` *"Blocks Save"* until `#1193`**, and `DemandSourceDetailPage` believed it —
 * three section editors disabled Save on any `error` while the wizard, corrected by `R3a` the same
 * week, did not. Same artifact, same validators, two answers to *does this stop me saving*.
 *
 * The reason blocking belongs nowhere near this taxonomy is the runtime: **execution never throws,
 * never refuses and never blocks.** A required VAST parameter with nothing in it goes out as
 * `pubid=`; an ORTB row is simply absent. A console stricter than the runtime would serve the
 * configuration in production and refuse to let the operator **test** it — withholding the one
 * piece of evidence that settles the question. Todd: *"If we block test, we block save. Errors and
 * warnings don't block save."*
 *
 * What does block is a short, separate list that lives on the **form** validators, not here: an
 * entered value that fails its field's validator, and the structurally impossible — no endpoint, an
 * endpoint that is not a URL, identity. *Block on an invalid value, never on a missing one.*
 */

import { findingLevel, type FindingKind } from '@finding-kind';
import { encodeLocation, type ExpressionLocation } from './expression-walk';

export type MappingIssueLevel = 'error' | 'warn' | 'info';

export interface MappingIssue {
  /**
   * **What kind of thing is wrong** (`3i`, `#1412`) — one of `FINDING_KINDS`, shared with the
   * validators in `libs/dto` and with authored validators (`#1205`). **Required**, so every
   * validator has to answer, and **the level is not stored beside it**: it follows the kind
   * ({@link issueLevel}, `D41`). A finding carrying both could say `error` about a kind declared
   * `warn`, which is two answers to *how wrong* on one object.
   */
  kind: FindingKind;
  /** Operator-facing sentence describing the problem. */
  message: string;
  /** The field / row / path the issue points at (rendered mono, de-emphasized). */
  ref?: string;
  /** True when the editor can auto-fix this — `ValidationSummary` renders a Fix button. */
  fixable?: boolean;
  /**
   * **Where inside the row's expression** (`4c`, `#1334`) — absent for a finding about the row
   * itself. A field inside an expression is drawn only while its row is open, so a finding that has
   * one is reached by **opening** the row at it, never by a reveal that changes nothing (pattern 38).
   */
  at?: ExpressionLocation;
}

/** Background tint per level, for banners. */
export const ISSUE_TINT: Record<MappingIssueLevel, string> = {
  error: 'error.tint',
  warn: 'warning.tint',
  info: 'info.tint',
};

/** Foreground ink per level, for banner text. */
export const ISSUE_INK: Record<MappingIssueLevel, string> = {
  error: 'error.ink',
  warn: 'warning.ink',
  info: 'info.ink',
};

/** Solid per level, for the status dot. */
export const ISSUE_DOT: Record<MappingIssueLevel, string> = {
  error: 'error.solid',
  warn: 'warning.solid',
  info: 'info.solid',
};

const RANK: Record<MappingIssueLevel, number> = { info: 1, warn: 2, error: 3 };

/** **How wrong a finding is — read from its kind, never stored on it** (`D41`, `3i`). */
export function issueLevel(issue: Pick<MappingIssue, 'kind'>): MappingIssueLevel {
  return findingLevel(issue.kind);
}

/** The worst of the issues — the one whose kind a single mark names — or `null` when there are none. */
export function worstIssue<T extends Pick<MappingIssue, 'kind'>>(issues: readonly T[]): T | null {
  let worst: T | null = null;
  for (const issue of issues) {
    if (!worst || RANK[issueLevel(issue)] > RANK[issueLevel(worst)]) worst = issue;
  }
  return worst;
}

/** The most severe level across the issues, or `null` when there are none. */
export function worstLevel(issues: readonly Pick<MappingIssue, 'kind'>[]): MappingIssueLevel | null {
  const worst = worstIssue(issues);
  return worst ? issueLevel(worst) : null;
}

/**
 * **What a row's own findings amount to** — the worst level, and the sentences behind it.
 *
 * The level places the dot; the detail is what it **says**. A mark that only carries a level tells
 * an operator that something is wrong on this row and makes them go and find out where — which is
 * the trip the mark exists to save. The sentence is the validator's own, never a second phrasing of
 * it: *where two surfaces make the same decision they share the sentence, not just the function*.
 *
 * It reaches both audiences or it reaches neither (`#1276`): the dot's accessible name **is** the
 * detail, not a restatement of its colour, so a keyboard or screen-reader user gets what the hover
 * gives. Colour alone was never the signal here, and now the label is not *"error on this row"* —
 * a phrase that named our severity vocabulary and described nothing.
 */
export interface RowSeverity {
  readonly level: MappingIssueLevel;
  /**
   * **The worst finding's kind** (`3i`), whose short label the value pill shows. One kind for the
   * row, not a list: the pill has room for two words, and the detail below names every finding.
   */
  readonly kind: FindingKind;
  /** Every sentence raised against this row, newline-separated — the validators' own wording. */
  readonly detail: string;
  /**
   * **The findings inside the row's expression, by where they are** (`3i`, Todd 2026-09-22): *a field
   * inside a shut row is marked at the row; when the row is expanded the nested field carries the
   * mark.* So the row's own mark above is the worst of its own and its children's, and this is what
   * the open editor draws at each place — keyed by {@link encodeLocation}, the token the editor
   * already stamps there for a reveal to find. Empty for a row whose findings are all about itself.
   */
  readonly nested: ReadonlyMap<string, NestedFinding>;
}

/** The mark one place inside an open row carries — the same three facts as the row's, for that place. */
export interface NestedFinding {
  readonly level: MappingIssueLevel;
  readonly kind: FindingKind;
  readonly detail: string;
}

/** The findings at each nested place, worst first per place. */
function nestedFindings(issues: readonly MappingIssue[]): ReadonlyMap<string, NestedFinding> {
  const byPlace = new Map<string, MappingIssue[]>();
  for (const issue of issues) {
    if (!issue.at || issue.at.length === 0) continue;
    const key = encodeLocation(issue.at);
    byPlace.set(key, [...(byPlace.get(key) ?? []), issue]);
  }
  const nested = new Map<string, NestedFinding>();
  for (const [key, list] of byPlace) {
    const worst = worstIssue(list)!;
    nested.set(key, { level: issueLevel(worst), kind: worst.kind, detail: list.map((i) => i.message).join('\n') });
  }
  return nested;
}

/**
 * **The worst level among the issues that point at one row**, or `null` when none do.
 *
 * `MappingRow` has had a `severity` slot since guidance §16 — a leading dot, documented as *"the
 * only mark that says something is wrong"* and *"never dropped under width pressure"*. Measured
 * 2026-09-22: **none of the five surfaces supplied it.** The mark was declared, rendered and read
 * by nothing — `#1017`'s family reached through a mark rather than a control, and invisible for the
 * usual reason: what a missing mark looks like is *no problems*.
 *
 * It is one helper rather than a `filter` at each call site because *which issues are about this
 * row* is one question. Each surface already answers it in the other direction — `holdsLeaf`,
 * `fixField`, `review.reaches` — and a second, per-caller spelling of the same match is how a row
 * comes to show a dot the Fix button cannot reach, or the reverse.
 *
 * **Exact match on the trimmed ref, deliberately.** Every validator raises a finding against the
 * trimmed name it read, so this matches what they wrote. A case-differing duplicate pair
 * (`Content-Type` beside `content-type`) marks the row the finding names and not its sibling — an
 * omission rather than a false mark, which is the direction this repo takes when a match is
 * uncertain (`#1002`). Folding case here would put a dot on a correct row whenever a differently
 * cased neighbour is the one at fault.
 *
 * **A finding whose `ref` names no row is not lost** — a required field nobody has added has a
 * `ref` and no row to sit on, and it is the summary's to state. This answers *which rows are
 * implicated*, never *which findings exist*.
 */
export function rowSeverity(issues: readonly MappingIssue[], ref: string | null | undefined): RowSeverity | null {
  const key = ref?.trim();
  if (!key) return null;
  const mine = issues.filter((issue) => issue.ref?.trim() === key);
  const worst = worstIssue(mine);
  return worst
    ? {
        level: issueLevel(worst),
        kind: worst.kind,
        detail: mine.map((issue) => issue.message).join('\n'),
        nested: nestedFindings(mine),
      }
    : null;
}

/** Count of issues at each level. */
export function countByLevel(issues: readonly MappingIssue[]): Record<MappingIssueLevel, number> {
  const counts: Record<MappingIssueLevel, number> = { error: 0, warn: 0, info: 0 };
  for (const issue of issues) counts[issueLevel(issue)] += 1;
  return counts;
}

/**
 * **Deleted, not renamed** (`#1193`). Nothing in this taxonomy blocks Save — see the severity
 * contract above — so a helper whose name asserts otherwise is the thing that has to go, rather
 * than a helper that answers *are there any errors* under a name nobody will re-read.
 *
 * Its three call sites were measured before removal: exactly **one** `level: 'error'` exists across
 * all five mapping validators, so two of them could never fire and the third blocked on a *missing*
 * `imp`. Use {@link worstLevel} or {@link countByLevel} to render severity.
 */
